// The route guard for everything but the sign-in page (D48): no session, or one that has
// expired, sends the user to /sign-in (and back afterwards); an account that must change its
// password is held on /account, shown as the choose-a-password page, until it has. Any API
// response saying the same (`x-ocs-auth`) does it too, so an expiry mid-use lands on the
// sign-in page.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { onAuthHint } from "../api/client";
import { ApiError } from "../api/queries";
import ChoosePasswordPage from "../pages/SignInChoosePassword";
import { useT } from "../i18n";
import { ErrorState, Loading } from "../ui/states";
import { meQuery } from "./api";

function signInPath(pathname: string, search: string): string {
  return `/sign-in?next=${encodeURIComponent(pathname + search)}`;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const t = useT().auth.guard;
  const me = useQuery(meQuery());
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(
    () =>
      onAuthHint((hint) => {
        if (hint === "sign-in") {
          queryClient.clear();
          void navigate(signInPath(location.pathname, location.search), { replace: true });
        } else if (location.pathname !== "/account") {
          void navigate("/account", { replace: true });
        }
      }),
    [navigate, queryClient, location.pathname, location.search],
  );

  if (me.isPending) return <Loading label={t.checkingSession} className="justify-center py-24" />;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <Navigate to={signInPath(location.pathname, location.search)} replace />;
    }
    return (
      <div className="mx-auto w-full max-w-lg px-4 py-24">
        <ErrorState title={t.sessionError} message={me.error.message} />
      </div>
    );
  }
  if (me.data.must_change_password) {
    if (location.pathname !== "/account") return <Navigate to="/account" replace />;
    return <ChoosePasswordPage email={me.data.email} />;
  }
  return <>{children}</>;
}
